import React, { ChangeEvent } from 'react';
import {
  Box,
  BoxProps,
  Text,
  Flex,
  FlexProps,
  Icon,
  NativeAttributes,
  AbstractButton,
  Tooltip,
} from 'pounceV2';
import Downshift, { ControllerStateAndHelpers } from 'downshift';
import Fuse from 'fuse.js';
import useEventHandler from 'Hooks/useEventHandler';
import usePrevious from 'Hooks/usePrevious';
import { useTheme } from '@mui/material';
import CompoundNumberInputV3 from './NumberInputV3';
import MenuItemsV3, { RenderItemProps } from './AutoCompleteInput/MenuItemsV3';
import CompoundNumberRangeInputV3, { CompoundNumberRangeInputProps } from './NumberRangeInputV3';
import {
  InputNavigationProvider,
  useInputNavigationContext,
} from './InputNavigationContext/InputNavigationContext';
import CompoundDateInputV3, {
  CompoundDateInputProps,
} from './CompoundDateInput/CompoundDateInputV3';
import CompoundDateRangeInputV3, {
  CompoundDateRangeInputProps,
} from './CompoundDateRangeInput/CompoundDateRangeInputV3';

export type CompoundInputChildElementProps = BoxProps &
  Omit<NativeAttributes<'input'>, 'onSelect'> & {
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    color?: string;
  };

export type CompoundInputProps = Partial<FlexProps> & {
  error?: string;
  backgroundColor?: string;
  children?:
    | React.ReactElement<CompoundInputChildElementProps>[]
    | React.ReactElement<CompoundInputChildElementProps>;
  onRemove?: () => void;
  disabled?: boolean;
};

const CompoundInputV3 = ({
  error,
  children,
  backgroundColor,
  onRemove,
  disabled = false,
  ...rest
}: CompoundInputProps) => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();
  return (
    <InputNavigationProvider>
      <Flex
        align="center"
        minHeight="38px"
        gap={1}
        backgroundColor={error ? tokens.background.semantic.warning[5] : backgroundColor}
        transition="background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
        borderRadius="large"
        p={2}
        {...rest}
      >
        {children}
        {(error || onRemove) && (
          <Flex gap={2} mr="0" ml="auto" align="center">
            {error && (
              <Tooltip content={error} arrow>
                <Icon type="alert-circle-filled" size="small" />
              </Tooltip>
            )}
            {!disabled && onRemove && (
              <AbstractButton
                onClick={onRemove}
                aria-label="Remove"
                data-tid="compound-input-remove-button"
              >
                <Flex align="center">
                  <Icon
                    size="small"
                    type="close"
                    color={componentTokens.tags.colors.condition.assets.closeBase}
                  />
                </Flex>
              </AbstractButton>
            )}
          </Flex>
        )}
      </Flex>
    </InputNavigationProvider>
  );
};

const AutoWidthInput = React.forwardRef<HTMLInputElement, CompoundInputChildElementProps>(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression for displayName
  function AutoWidthInput(props: CompoundInputChildElementProps, ref) {
    const {
      palette: { componentTokens },
    } = useTheme();
    const { focusNextInput, focusPreviousInput, dataAttribute } = useInputNavigationContext();
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const inputElement = e.target as HTMLInputElement;
        const { selectionStart, selectionEnd, value } = e.currentTarget;
        const isCursorAtEnd = selectionStart === value?.length && selectionEnd === value?.length;
        const isCursorAtStart = selectionStart === 0 && selectionEnd === 0;
        // If the user presses backspace and the cursor is at the start, focus the previous input
        if ((e.key === 'Backspace' || e.key === 'ArrowLeft') && isCursorAtStart) {
          focusPreviousInput(inputElement);
          e.preventDefault();
        }
        // If the user presses right arrow and the cursor is at the end, focus the next input
        if (e.key === 'ArrowRight' && isCursorAtEnd) {
          focusNextInput(inputElement);
          e.preventDefault();
        }
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
      },
      [focusNextInput, focusPreviousInput, props],
    );

    return (
      // We need to use a div here because the input element doesn't scale to fit its content
      // We can potentially replace this with a div with contenteditable="true" in the future
      <Box position="relative">
        <Box
          whiteSpace="pre-wrap"
          wordBreak="break-all"
          as="div"
          p={0}
          color={componentTokens.tags.colors.condition.text.labelBlue}
          visibility="hidden"
          height="18px"
          overflow="hidden"
          fontSize="medium"
          fontWeight="medium"
          textOverflow="ellipsis"
        >
          {props.value || props.placeholder}
        </Box>
        {/* We have to use a Box here since Pounce doesn't export an appropriate Input Element.
        We can replace this with <InputElement> once we move this component to Pounce */}
        <Box
          ref={ref}
          as="input"
          position="absolute"
          color={componentTokens.tags.colors.condition.text.labelBase}
          left={0}
          right={0}
          top={0}
          bottom={0}
          opacity={1}
          fontSize="medium"
          fontWeight="medium"
          backgroundColor="transparent"
          border="0"
          // Same placeholder treatment as the standard TextInput (MuiTextField theme) —
          // the previous 0.2-opacity static token was barely legible on the chip background
          _placeholder={{
            color: componentTokens.inputs.colors.normal.text.inputText.selected,
          }}
          p={0}
          width="100%"
          height="100%"
          {...props}
          onKeyDown={handleKeyDown}
          {...dataAttribute}
        />
      </Box>
    );
  },
);

export type BasicInputProps = CompoundInputChildElementProps & {
  suffix?: string;
  affixColor?: string;
};

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression for displayName
const BasicInput = React.forwardRef<HTMLInputElement, BasicInputProps>(function BasicInput(
  { prefix, affixColor, suffix, onChange, ...rest }: BasicInputProps,
  ref,
) {
  return (
    <Flex>
      {prefix && (
        <Text color={affixColor} fontWeight="medium" as="span">
          {prefix}
        </Text>
      )}
      <AutoWidthInput {...rest} ref={ref} onChange={onChange} />
      {suffix && (
        <Text color={affixColor} fontWeight="medium" as="span">
          {suffix}
        </Text>
      )}
    </Flex>
  );
});

export type AutoCompleteInputProps<T> = {
  items: T[];
  value?: T | null;
  itemToString?: (item: T) => string;
  /**
   * A function that filters the items displayed in the dropdown. If not provided, the default function will be used.
   */
  itemFilterFunction?: (
    item: T,
    inputValue: string | null,
    safeItemToString: (item: T | null) => string,
  ) => boolean;
  maxResults?: number;
  onSelect?: (
    item: T | null,
    helpers?: ControllerStateAndHelpers<T>,
  ) => Promise<any> | boolean | void;
  fuzzySearch?: boolean;
  /**
   * A function that renders items in the dropdown. If not provided, the itemToString function will be used.
   */
  renderMenuItem?: (item: T) => React.ReactNode;
  /**
   * A function that returns a unique key for each item. If not provided, the itemToString function will be used.
   */
  itemToKey?: (item: T) => string;
  /**
   * The title of the dropdown.
   */
  menuTitle?: string | React.ReactNode;
  /**
   * A function used to group the Menu Items.
   */
  itemToGroup?: (item: T) => string;
  /**
   * If true, the user can type in a value that is not in the list of items.
   */
  allowFreeInput?: boolean;
  /**
   * If true, show a caret icon for the dropdown
   */
  showCaret?: boolean;
  /**
   * If true, use a virtualized list of options. Useful when there are thousands of options
   */
  shouldVirtualize?: boolean;
  /**
   * An optional function that returns an array of options based on the user's input. This is only used
   * when the allowFreeInput boolean is true.
   */
  getItemsForDisplay?: (
    inputValue: string | null,
    filteredItems: T[],
    downshiftRef: Downshift<T>,
  ) => T[];
  /**
   * An optional function used to compare the value with an item from the items array. Used to find the highlighted item.
   */
  comparisonFunction?: (item: T, value: T | null) => boolean;
  /**
   * A function that handles the keyDown event. It's passed the event and list of filtered items.
   */
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement>,
    downshiftProps: ControllerStateAndHelpers<T>,
    filteredItems: T[],
    highlightedItem: T,
  ) => void;
} & Omit<CompoundInputChildElementProps, 'onKeyDown' | 'onSelect'>;
// TODO: Auto-select item when inputValue matches one exactly?
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression for displayName
const AutoCompleteInput = React.forwardRef(function AutoCompleteInput<T>(
  {
    items,
    value,
    onChange,
    allowFreeInput,
    getItemsForDisplay = null,
    comparisonFunction = null,
    itemToString = v => String(v),
    maxResults = Number.MAX_SAFE_INTEGER,
    onSelect,
    fuzzySearch = false,
    renderMenuItem,
    menuTitle = 'Select a condition from the list',
    itemToKey = itemToString,
    itemToGroup,
    itemFilterFunction,
    showCaret = false,
    shouldVirtualize = false,
    ...rest
  }: AutoCompleteInputProps<T>,
  forwardRef,
) {
  const {
    palette: { componentTokens },
  } = useTheme();
  const prevValue = usePrevious(value);
  const fallbackRef = React.useRef<HTMLInputElement>(null);
  const downshiftRef = React.useRef<Downshift<T>>(null);
  const ref = forwardRef || fallbackRef;
  const { focusNextInput } = useInputNavigationContext();
  const safeItemToString = React.useCallback(
    (item: T | null) => {
      // eslint-disable-next-line
      if (item == undefined) {
        return '';
      }
      // If the user is allowed to type in a value that is not in the list of items, we want to
      // return the value as is.
      if (typeof item === 'string' && !items.includes(item)) {
        return item;
      }
      return itemToString(item); // eslint-disable-line eqeqeq
    },
    [itemToString, items],
  );
  const [inputValue, setInputValue] = React.useState(safeItemToString(value));

  // We map the items to a new type in order to feed it to the fuzzySearch generic function.
  const itemsToSearch = React.useMemo(
    () =>
      new Fuse(
        items.map(i => ({
          // Contains the string representation of the item that will be tested.
          searchString: itemToGroup
            ? `${itemToGroup(i)}${safeItemToString(i)}`
            : safeItemToString(i),
          // Include the actual item in the object so we can map it back after we are done with the search.
          item: i,
        })),
        { keys: ['searchString'] },
      ),
    [itemToGroup, items, safeItemToString],
  );

  const filteredItems = React.useMemo(() => {
    if (inputValue && fuzzySearch) {
      return (
        itemsToSearch
          .search(inputValue, {
            limit: maxResults,
          })
          // extract the item from fuse result https://fusejs.io/demo.html
          .map(s => s.item.item)
      );
    }
    return itemFilterFunction
      ? items.filter(item =>
          inputValue ? itemFilterFunction(item, inputValue, safeItemToString) : true,
        )
      : items.filter(item =>
          inputValue
            ? safeItemToString(item).toLowerCase().startsWith(inputValue.toLowerCase())
            : true,
        );
  }, [
    inputValue,
    fuzzySearch,
    itemFilterFunction,
    items,
    itemsToSearch,
    maxResults,
    safeItemToString,
  ]);

  const itemsForDisplay = React.useMemo(() => {
    return allowFreeInput && Boolean(getItemsForDisplay)
      ? getItemsForDisplay(inputValue, filteredItems, downshiftRef.current)
      : filteredItems;
  }, [allowFreeInput, filteredItems, getItemsForDisplay, inputValue]);

  const handleOnSelect = React.useCallback(
    (item: T, helpers: ControllerStateAndHelpers<T>) => {
      if (!item) {
        return;
      }
      setInputValue(safeItemToString(item));
      if (onSelect) {
        const shouldReturn = onSelect(item, helpers);

        // We need to opt out of focusing on the next input sometimes. Returning true from the onSelect function
        // support this.
        if (shouldReturn === true) {
          return;
        }
        // There is no way to focus the next input in the same event loop, so we have to wait for the next tick.
        // This is a hack that we have to do because we are conditionally rendering the inputs. At the time of change,
        // the next input might not be rendered yet.
        setTimeout(() => {
          focusNextInput(ref.current);
        }, 1);
      }
    },
    [safeItemToString, onSelect, focusNextInput, ref],
  );

  const handleFocus = useEventHandler(downshiftProps => {
    // When the input is focused, we want to open the menu and clear the input value
    // in order to show all the items, unless the user has typed a free text value.
    // When the input is blurred, the value will be reset to the selected item.
    downshiftProps.openMenu();

    // We don't want to clean the input if the chip is a free text chip
    const shouldClearInput =
      !allowFreeInput ||
      (typeof value !== 'string' &&
        items.some(i => safeItemToString(i) === safeItemToString(value)));

    if (shouldClearInput) {
      setInputValue('');
    }

    // We also want to highlight the current value
    if (value) {
      const highlightedIndex = items.findIndex(item => {
        if (
          comparisonFunction &&
          value !== undefined &&
          value !== null &&
          typeof value !== 'string'
        ) {
          return comparisonFunction(item, value);
        }

        return safeItemToString(item) === safeItemToString(value);
      });
      downshiftProps.setHighlightedIndex(highlightedIndex !== -1 ? highlightedIndex : 0);
    }
  });

  React.useEffect(() => {
    if (prevValue !== value) {
      setInputValue(safeItemToString(value));
    }
  }, [setInputValue, safeItemToString, value, items, prevValue]);

  // Autofocus on mount if input is empty
  React.useEffect(() => {
    if (!value && ref && 'current' in ref && ref.current) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const stateReducer = (state, changes) => {
    // When the user presses the Escape key, we want to close the menu
    // without clearing the input value.
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEscape:
        return {
          ...state,
          isOpen: false,
        };
      case Downshift.stateChangeTypes.changeInput:
        return {
          ...state,
          // Highlight the first item by default
          highlightedIndex:
            changes.inputValue === '' || (!allowFreeInput && filteredItems.length === 0) ? null : 0,
        };
      default:
        return changes;
    }
  };

  return (
    <Downshift<T>
      ref={downshiftRef}
      itemToString={safeItemToString}
      onSelect={handleOnSelect}
      stateReducer={stateReducer}
      selectedItem={value}
      inputValue={inputValue}
      onInputValueChange={inputVal => setInputValue(inputVal)}
    >
      {downshiftProps => {
        const {
          isOpen,
          getMenuProps,
          getInputProps,
          getRootProps,
          closeMenu,
          selectedItem,
          getItemProps,
        } = downshiftProps;
        return (
          <Box position="relative" {...getRootProps()}>
            <Flex gap={2} align="center">
              <AutoWidthInput
                {...getInputProps({
                  ref,
                  ...rest,
                  placeholder: safeItemToString(selectedItem) || rest.placeholder, // eslint-disable-line eqeqeq
                  onClick: e => {
                    handleFocus(downshiftProps);
                    if (rest.onClick) {
                      rest.onClick(e);
                    }
                  },
                  onFocus: e => {
                    handleFocus(downshiftProps);
                    if (rest.onFocus) {
                      rest.onFocus(e);
                    }
                  },
                  onChange: (e: React.FormEvent<HTMLInputElement>) => {
                    if (onChange) {
                      onChange(e as ChangeEvent<HTMLInputElement>);
                    }
                    setInputValue(e.currentTarget.value);
                  },
                  onBlur: e => {
                    closeMenu();
                    if (!allowFreeInput || (inputValue === '' && selectedItem)) {
                      onSelect(selectedItem);
                      setInputValue(safeItemToString(selectedItem));
                    }
                    if (rest.onBlur) {
                      rest.onBlur(e);
                    }
                  },
                  onKeyDown: e => {
                    if (rest.onKeyDown) {
                      // Pass in the downshiftProps, filteredItems and highlightedItem so the onKeyDown handler has access to them
                      const highlightedItem = itemsForDisplay[downshiftProps.highlightedIndex];
                      rest.onKeyDown(e, downshiftProps, filteredItems, highlightedItem);
                    }
                  },
                })}
              />
              {showCaret && (
                <Icon
                  aria-label="Show menu"
                  size="small"
                  type="caret-down"
                  onClick={() => downshiftProps.openMenu()}
                />
              )}
            </Flex>
            <Box
              backgroundColor={componentTokens.selectMenuItems.color.item.background.containerBase}
              borderColor={componentTokens.selectMenuItems.color.item.stroke.container.resting}
              boxShadow="0px 8px 12px 0px rgba(9, 9, 10, 0.3)"
              position="absolute"
              borderRadius="large"
              {...getMenuProps()}
              zIndex="dropdown"
              left={0}
              top={24}
            >
              {isOpen && (
                <Box
                  borderRadius="large"
                  border="1px solid"
                  borderColor={componentTokens.selectMenuItems.color.item.stroke.header.selected}
                  width="max-content"
                  maxWidth={Math.max(ref.current?.offsetWidth, 800)}
                >
                  <Box
                    p={3}
                    borderTopLeftRadius="large"
                    borderTopRightRadius="large"
                    borderRadius={filteredItems.length ? undefined : 'large'}
                    backgroundColor={
                      componentTokens.selectMenuItems.color.item.background.header.selected
                    }
                  >
                    {menuTitle}
                  </Box>
                  <MenuItemsV3
                    shouldVirtualize={shouldVirtualize}
                    itemToGroup={itemToGroup}
                    items={itemsForDisplay}
                    renderItem={({ item, isNested, style }: RenderItemProps<T>) => (
                      <Box
                        as="li"
                        cursor="pointer"
                        py={3}
                        px={isNested ? 5 : 3}
                        sx={{
                          ...style,
                          // downshift adds aria-selected="true" to the active item
                          '&[aria-selected="true"]': {
                            backgroundColor:
                              componentTokens.selectMenuItems.color.item.background.row.selected,
                          },
                        }}
                        key={itemToKey(item)}
                        {...getItemProps({ item })}
                      >
                        {typeof renderMenuItem === 'function'
                          ? renderMenuItem(item)
                          : safeItemToString(item)}
                      </Box>
                    )}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      }}
    </Downshift>
  );
});

AutoCompleteInput.displayName = 'AutoCompleteInput';

CompoundInputV3.Input = BasicInput as React.FC<BasicInputProps>;
CompoundInputV3.AutoCompleteInput = AutoCompleteInput as <T>(
  props: AutoCompleteInputProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => JSX.Element;
CompoundInputV3.NumberInput = CompoundNumberInputV3;
CompoundInputV3.NumberRangeInput =
  CompoundNumberRangeInputV3 as React.FC<CompoundNumberRangeInputProps>;
CompoundInputV3.DateInput = CompoundDateInputV3 as React.FC<CompoundDateInputProps>;
CompoundInputV3.DateRangeInput = CompoundDateRangeInputV3 as React.FC<CompoundDateRangeInputProps>;

export default CompoundInputV3;
